import { observer } from "mobx-react";
import * as React from "react";
import styled, { css } from "styled-components";

import Style from "@client/utils/Style";

type WrapperProps = {
  disabled?: boolean;
  justifyContent?: string;
  width?: string;
};
const Wrapper = styled.div<WrapperProps>`
  display: flex;
  height: ${Style.inputSizes.regular.height}px;
  align-items: center;
  ${props => (props.disabled ? "opacity: 0.5;" : "")}
  ${props =>
    props.justifyContent ? `justify-content: ${props.justifyContent};` : ""}
  ${props => (props.width ? `width: ${props.width};` : "")}
`;
type InputWrapperProps = {
  height: number;
  disabled?: boolean;
  hideFocusIndicator?: boolean;
};
const InputWrapper = styled.div<InputWrapperProps>`
  position: relative;
  width: ${props => props.height * 2}px;
  height: ${props => props.height}px;
  ${props => (!props.disabled ? "cursor: pointer;" : "")};
  &:focus {
    ${props =>
      props.hideFocusIndicator
        ? css`
            outline: none;
          `
        : ""};
  }
`;
const inputBackground = (props: {
  big?: boolean;
  checked?: boolean;
  secondary?: boolean;
}) => {
  if (props.big) {
    if (props.checked) {
      return Style.palette.alert;
    }
    return Style.palette.primaryHighlight;
  }

  if (props.secondary && props.checked) {
    return Style.palette.grayscale[5];
  }

  if (props.checked) {
    return Style.palette.primaryHighlight;
  }

  return "white";
};
const getInputBorderColor = (props: {
  big?: boolean;
  checked?: boolean;
  secondary?: boolean;
  triState?: boolean;
}) => {
  if (props.big && props.checked) {
    return Style.palette.alert;
  }

  if (props.secondary) {
    return Style.palette.grayscale[5];
  }

  if (props.triState && props.checked == null) {
    return Style.palette.grayscale[2];
  }

  return Style.palette.primaryHighlight;
};
type InputBackgroundProps = {
  big?: boolean;
  checked?: boolean;
  secondary?: boolean;
  triState?: boolean;
  height: number;
};
const InputBackground = styled.div<InputBackgroundProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: all ${Style.animations.swift};
  background: ${props => inputBackground(props)};
  border-radius: 50px;
  border: 1px solid ${props => getInputBorderColor(props)};
`;
const getThumbLeft = (props: {
  checked?: boolean;
  triState?: boolean;
  height: number;
}) => {
  if (props.checked) {
    return props.height - 1;
  }

  if (props.triState && props.checked == null) {
    return props.height / 2 - 1;
  }

  return -1;
};
type InputThumbProps = {
  checked?: boolean;
  triState?: boolean;
  height: number;
  big?: boolean;
  secondary?: boolean;
};
const InputThumb = styled.div<InputThumbProps>`
  position: absolute;
  width: ${props => props.height - 2}px;
  height: ${props => props.height - 2}px;
  transition: all ${Style.animations.swift};
  top: -1px;
  left: ${props => getThumbLeft(props)}px;
  background: white;
  border: 1px solid ${props => getInputBorderColor(props)};
  border-radius: 50%;
`;
const Label = styled.div<{ secondary?: boolean }>`
  margin-left: 16px;
  ${props => (props.secondary ? `color: ${Style.palette.grayscale[4]};` : "")}
  white-space: nowrap;
`;
const LeftLabel = styled.div`
  margin-right: 16px;
  color: ${Style.palette.grayscale[4]};
  white-space: nowrap;
`;

type Props = {
  onChange: (checked: boolean | null) => void;
  checked: boolean;
  disabled: boolean;
  triState: boolean;
  leftLabel: boolean;
  secondary: boolean;
  noLabel: boolean;
  height?: number;
  width: string;
  justifyContent: string;
  big: boolean;
  checkedLabel?: string;
  uncheckedLabel?: string;
  hideFocusIndicator: boolean;
  style: { [key: string]: any };
};
type State = {
  lastChecked: boolean | null;
};
@observer
class Switch extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    checkedLabel: "Yes",
    uncheckedLabel: "No",
    height: 16,
  };

  state: State = {
    lastChecked: null,
  };

  getLabel() {
    let label = this.props.checked
      ? this.props.checkedLabel
      : this.props.uncheckedLabel;
    if (this.props.triState && this.props.checked == null) {
      label = "Not Set";
    }

    return label;
  }

  handleInputChange() {
    if (this.props.disabled) {
      return;
    }

    if (this.props.triState) {
      if (this.props.checked === true || this.props.checked === false) {
        this.setState({ lastChecked: this.props.checked });
        this.props.onChange(null);
      } else {
        this.props.onChange(!this.state.lastChecked);
      }
    } else {
      this.props.onChange(!this.props.checked);
    }
  }

  handleKeyDown(evt: React.KeyboardEvent) {
    if (evt.key !== " ") {
      return;
    }
    evt.preventDefault();

    this.handleInputChange();
  }

  renderInput() {
    const actualHeight = this.props.height || 16;
    return (
      <InputWrapper
        height={actualHeight}
        disabled={this.props.disabled}
        onClick={() => {
          this.handleInputChange();
        }}
        tabIndex={0}
        onKeyDown={evt => {
          this.handleKeyDown(evt);
        }}
        hideFocusIndicator={this.props.hideFocusIndicator}
      >
        <InputBackground
          triState={this.props.triState}
          big={this.props.big}
          checked={this.props.checked}
          height={actualHeight}
          secondary={this.props.secondary}
        >
          <InputThumb
            triState={this.props.triState}
            big={this.props.big}
            checked={this.props.checked}
            height={actualHeight}
            secondary={this.props.secondary}
          />
        </InputBackground>
      </InputWrapper>
    );
  }

  renderLeftLabel() {
    if (!this.props.leftLabel || this.props.noLabel) {
      return null;
    }

    return <LeftLabel>{this.getLabel()}</LeftLabel>;
  }

  renderRightLabel() {
    if (this.props.big || this.props.leftLabel || this.props.noLabel) {
      return null;
    }

    return <Label secondary={this.props.secondary}>{this.getLabel()}</Label>;
  }

  render() {
    return (
      <Wrapper
        disabled={this.props.disabled}
        style={this.props.style}
        width={this.props.width}
        justifyContent={this.props.justifyContent}
      >
        {this.renderLeftLabel()}
        {this.renderInput()}
        {this.renderRightLabel()}
      </Wrapper>
    );
  }
}

export default Switch;
